import React from "react";

/**
 * Utility functions for persisting date range selections across page navigation
 */

/**
 * Get the default yesterday date range
 * @returns {Object} Default date range object
 */
export const getDefaultYesterday = () => ({
    preset: "yesterday",
    startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
    endDate: new Date(new Date().setDate(new Date().getDate() - 1)),
    isCustom: false,
});

/**
 * Load date range from localStorage for a specific page
 * Clears localStorage on page refresh to reset to default
 * @param {string} pageKey - Unique key for the page (e.g., 'dailyReport', 'stripeDashboard')
 * @returns {Object} Date range object
 */
export const loadDateRangeFromStorage = (pageKey) => {
    // Try to load saved date range from localStorage
    try {
        const savedDateRange = localStorage.getItem(`${pageKey}DateRange`);
        if (savedDateRange) {
            const parsed = JSON.parse(savedDateRange);
            console.log(`Loaded saved date range for ${pageKey}:`, parsed);
            // Convert string dates back to Date objects
            return {
                ...parsed,
                startDate: new Date(parsed.startDate),
                endDate: new Date(parsed.endDate),
            };
        }
    } catch (error) {
        console.warn(`Failed to load saved date range for ${pageKey} from localStorage:`, error);
    }

    // Return default yesterday if no saved date range
    return getDefaultYesterday();
};

/**
 * Save date range to localStorage for a specific page
 * @param {string} pageKey - Unique key for the page (e.g., 'dailyReport', 'stripeDashboard')
 * @param {Object} dateRange - Date range object to save
 */
export const saveDateRangeToStorage = (pageKey, dateRange) => {
    try {
        localStorage.setItem(`${pageKey}DateRange`, JSON.stringify(dateRange));
    } catch (error) {
        console.warn(`Failed to save date range for ${pageKey} to localStorage:`, error);
    }
};

/**
 * Clear date range from localStorage for a specific page
 * @param {string} pageKey - Unique key for the page (e.g., 'dailyReport', 'stripeDashboard')
 * @returns {boolean} True if successfully cleared, false otherwise
 */
export const clearDateRangeFromStorage = (pageKey) => {
    try {
        localStorage.removeItem(`${pageKey}DateRange`);
        console.log(`Successfully cleared date range for ${pageKey} from localStorage`);
        return true;
    } catch (error) {
        console.warn(`Failed to clear date range for ${pageKey} from localStorage:`, error);
        return false;
    }
};

/**
 * Clear all date ranges from localStorage (for all pages)
 * @returns {number} Number of date ranges cleared
 */
export const clearAllDateRangesFromStorage = () => {
    let clearedCount = 0;
    try {
        // Get all localStorage keys
        const keys = Object.keys(localStorage);
        
        // Filter keys that end with 'DateRange'
        const dateRangeKeys = keys.filter(key => key.endsWith('DateRange'));
        
        // Remove each date range key
        dateRangeKeys.forEach(key => {
            localStorage.removeItem(key);
            clearedCount++;
        });
        
        console.log(`Successfully cleared ${clearedCount} date ranges from localStorage:`, dateRangeKeys);
        return clearedCount;
    } catch (error) {
        console.warn('Failed to clear all date ranges from localStorage:', error);
        return clearedCount;
    }
};

/**
 * Create a custom hook for date range persistence
 * @param {string} pageKey - Unique key for the page
 * @returns {Array} [dateRange, setDateRange, handleDateRangeChange]
 */
export const useDateRangePersistence = (pageKey) => {
    const [dateRange, setDateRange] = React.useState(() => loadDateRangeFromStorage(pageKey));

    const handleDateRangeChange = (rangeData) => {
        setDateRange(rangeData);
        saveDateRangeToStorage(pageKey, rangeData);
    };

    return [dateRange, setDateRange, handleDateRangeChange];
};
